// Shared Currency enum <-> label helpers, so components don't each redefine the map.
import { Currency } from '../models/bank-account/bank-account';

// Re-exported so currency-related consumers have a single import source.
export { Currency };

export const CURRENCY_LABELS: Record<Currency, string> = {
  [Currency.USD]: 'USD',
  [Currency.HNL]: 'HNL',
  [Currency.EUR]: 'EUR',
};

/**
 * The symbol each currency is written with, for prefixing an amount.
 *
 * <p>Separate from {@link CURRENCY_LABELS} because the two answer different questions: a label NAMES a
 * currency ("this figure is in HNL") and belongs beside a foreign amount, while a symbol MARKS one and
 * belongs in front of every figure in a column already stated in it. A column of "L 1,234.00" reads; a
 * column of "HNL 1,234.00" is an announcement repeated on every row.</p>
 */
export const CURRENCY_SYMBOLS: Record<Currency, string> = {
  [Currency.USD]: '$',
  [Currency.HNL]: 'L',
  [Currency.EUR]: '€',
};

/** Currency enum -> symbol (e.g. Currency.USD -> "$"); '' when null/unknown. */
export function currencySymbol(currency: number | null | undefined): string {
  return currency == null ? '' : (CURRENCY_SYMBOLS[currency as Currency] ?? '');
}

/** Currency options for selects ({ label, value }). */
export const CURRENCY_OPTIONS: { label: string; value: Currency }[] = (Object.entries(CURRENCY_LABELS) as [string, string][]).map(
  ([value, label]) => ({ label, value: Number(value) as Currency }),
);

/** Currency enum -> display code (e.g. Currency.HNL -> "HNL"); '' when null/unknown. */
export function currencyLabel(currency: number | null | undefined): string {
  return currency == null ? '' : (CURRENCY_LABELS[currency as Currency] ?? '');
}

/** Display code -> Currency enum (e.g. "HNL" -> Currency.HNL); null when unknown. */
export function currencyFromCode(code: string | null | undefined): Currency | null {
  if (!code) return null;
  const upper = code.toUpperCase();
  const entry = (Object.entries(CURRENCY_LABELS) as [string, string][]).find(([, label]) => label === upper);
  return entry ? (Number(entry[0]) as Currency) : null;
}

/**
 * Normalizes whatever the API hands back for a currency.
 *
 * <p>FinancialAccountDto exposes `Currency` as the enum while the entity stores a string, and
 * JsonStringEnumConverter is disabled — so a read yields the NUMBER (1) and a write must send the CODE
 * ("HNL"). Accepts both so this keeps working if that converter is ever switched on.</p>
 */
export function currencyFromApi(value: number | string | null | undefined): Currency | null {
  if (value == null || value === '') return null;
  if (typeof value === 'number') return CURRENCY_LABELS[value as Currency] !== undefined ? (value as Currency) : null;
  const numeric = Number(value);
  if (!Number.isNaN(numeric) && CURRENCY_LABELS[numeric as Currency] !== undefined) return numeric as Currency;
  return currencyFromCode(value);
}

/** Display code for whatever the API returned (number or code). '' when unknown. */
export function currencyLabelFromApi(value: number | string | null | undefined): string {
  return currencyLabel(currencyFromApi(value));
}
